import React, { createContext, useContext, useMemo } from 'react';
import { StyleProp, Text, TextStyle } from 'react-native';

import { Locale } from '@providers/I18n';
import { StyleObj } from '@providers/Theme/types';
import { Theme } from '@providers/Theme/theme';
import { makeStyles } from '@providers/Theme';
import { Direction, useDirection } from '@providers/Direction';

export type WeightAlias =
  | 'extraLight' // 200
  | 'light' // 300
  | 'regular' // 400
  | 'medium' // 500
  | 'semiBold' // 600
  | 'bold' // 700
  | 'extraBold'; // 800

type EllipsizeValue = 'head' | 'tail' | 'middle' | 'clip';

const fontFamilyOfFontWeightAlias: Record<Locale, Record<WeightAlias, string>> =
  {
    en: {
      extraLight: 'Gilroy-Light',
      light: 'Gilroy-Light',
      regular: 'Gilroy-Light',
      medium: 'Gilroy-Medium',
      semiBold: 'Gilroy-SemiBold',
      bold: 'Gilroy-SemiBold',
      extraBold: 'Gilroy-ExtraBold',
    },
    ar: {
      extraLight: 'Cairo-ExtraLight',
      light: 'Cairo-Light',
      regular: 'Cairo-Regular',
      medium: 'Cairo-Medium',
      semiBold: 'Cairo-SemiBold',
      bold: 'Cairo-Bold',
      extraBold: 'Cairo-ExtraBold',
    },
  };

export type SizeOptions = '4xl' | '3xl' | '2xl' | 'xl' | 'md' | 'sm' | 'xs';
export type ResponsiveSizeOptions = {
  xs: SizeOptions;
  sm?: SizeOptions;
  md?: SizeOptions;
  lg?: SizeOptions;
  xl?: SizeOptions;
};

export type SizeValue = SizeOptions | ResponsiveSizeOptions;

export type Variant =
  | 'primary'
  | 'secondary'
  | 'cool'
  | 'error'
  | 'warning'
  | 'success'
  | 'base'
  | 'occean';

type TypographyStylesProps = {
  weight: WeightAlias;
  direction: Direction;
  size: SizeValue;
  locale: Locale;
  style?: StyleObj;
  variant?: Variant;
  color?: string;
};

const sizeOptionsToStylesMap: Record<SizeOptions, StyleObj> = {
  '4xl': { fontSize: 48, lineHeight: 60 },
  '3xl': { fontSize: 30, lineHeight: 38 },
  '2xl': { fontSize: 24, lineHeight: 32 },
  xl: { fontSize: 18, lineHeight: 28 },
  sm: { fontSize: 16, lineHeight: 20 },
  xs: { fontSize: 18, lineHeight: 12 },
  md: { fontSize: 16, lineHeight: 24 },
};

const getResponsiveFontSizeOptions = (size: SizeValue) => {
  if (typeof size === 'string') {
    return {
      xs: sizeOptionsToStylesMap[size],
    };
  }

  return {
    xs: sizeOptionsToStylesMap[size.xs],
    ...(size.sm && { sm: sizeOptionsToStylesMap[size.sm] }),
    ...(size.md && { md: sizeOptionsToStylesMap[size.md] }),
    ...(size.lg && { lg: sizeOptionsToStylesMap[size.lg] }),
    ...(size.xl && { xl: sizeOptionsToStylesMap[size.xl] }),
  };
};

const variantColorMap: Record<Variant, (theme: Theme) => string | undefined> = {
  secondary: (theme) => theme.palette.grey[700],
  cool: (theme) => theme.palette.cool[700],
  error: (theme) => theme.palette.error[300],
  warning: (theme) => theme.palette.warning[400],
  success: (theme) => theme.palette.success[400],
  primary: (theme) => theme.palette.base[900],
  base: (theme) => theme.palette.base[100],
  occean: (theme) => theme.palette.ocean[700],
};

const useStyles = makeStyles<TypographyStylesProps>((theme) => ({
  root: (props) => ({
    fontFamily: fontFamilyOfFontWeightAlias[props.locale][props.weight],
    // fontWeight: numericWeightOfFontWeightAlias[props.weight],
    color: props.variant ? variantColorMap[props.variant](theme) : 'inherit',
    textAlign: props.direction === 'rtl' ? 'right' : 'left',
    on: getResponsiveFontSizeOptions(props.size),
  }),
}));

export type TypographyProps = {
  testID?: string;
  children: React.ReactNode;
  weight?: WeightAlias;
  direction?: Direction;
  size?: SizeValue;
  style?: Exclude<StyleProp<TextStyle>, false>;
  variant?: Variant;
  locale?: Locale; // we guess locale from app direction, but give only prop to override
  numberOfLines?: number;
  ellipsizeMode?: EllipsizeValue;
};

type TypographyContextValue = Pick<
  TypographyProps,
  'size' | 'weight' | 'variant'
>;

const TypographyContext = createContext<TypographyContextValue>({
  size: 'md',
  weight: 'medium',
});

const useTypographyContext = () => useContext(TypographyContext);

export const Typography: React.FC<TypographyProps> = ({
  testID,
  children,
  direction: directionFromProps,
  locale: localeFromProps,
  size: sizeFromProps,
  weight: weightFromProps,
  variant: variantFromProps,
  style,
  numberOfLines,
  ellipsizeMode,
}) => {
  const inhertiedTypographyProps = useTypographyContext();
  const size = sizeFromProps ?? inhertiedTypographyProps.size ?? 'md';
  const weight = weightFromProps ?? inhertiedTypographyProps.weight ?? 'medium';
  const variant = variantFromProps ?? inhertiedTypographyProps.variant;
  const { direction: appDirection } = useDirection();
  const direction = directionFromProps ?? appDirection;
  const locale = localeFromProps ?? (direction === 'ltr' ? 'en' : 'ar');

  const styles = useStyles({ weight, direction, size, variant, locale });

  const typographyContextValue = useMemo(
    () => ({
      size,
      weight,
      variant,
    }),
    [size, weight, variant],
  );
  return (
    <TypographyContext.Provider value={typographyContextValue}>
      <Text
        testID={testID}
        ellipsizeMode={ellipsizeMode}
        style={[styles.root, style]}
        numberOfLines={numberOfLines}>
        {children}
      </Text>
    </TypographyContext.Provider>
  );
};
